import React, { useMemo } from "react";
import { Autocomplete, AutocompleteRenderInputParams, TextField } from "@mui/material";
import { useActiveUsers, WorkflowActiveUser } from "./useActiveUsers";

interface WorkflowUserPickerProps {
  label: string;
  /** Portal user id(s) as text — one id, or a comma-separated list when `multiple`. */
  value: string;
  onChange: (value: string) => void;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
}

const parseIds = (value: string) =>
  value
    .split(",")
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);

/**
 * Picks Portal users by id over the active-users list. Stored as ids, not names, because that is
 * what the runtime resolves a "specific user" from (Epic 80421's PersonResolver / TaskStageHandler).
 * An id that isn't in the list (a deactivated user) is still shown, as "User #id", so it can be
 * seen and removed rather than silently dropped.
 */
const WorkflowUserPicker: React.FC<WorkflowUserPickerProps> = ({
  label,
  value,
  onChange,
  multiple = false,
  disabled = false,
  placeholder,
  helperText,
}) => {
  const { users, loading } = useActiveUsers();

  const selected = useMemo(
    () =>
      parseIds(value).map(
        (id) => users.find((user) => user.userId === id) ?? { userId: id, userName: `User #${id}` },
      ),
    [users, value],
  );

  const sharedProps = {
    fullWidth: true,
    size: "small" as const,
    disabled,
    loading,
    options: users,
    getOptionLabel: (option: WorkflowActiveUser) => option.userName,
    isOptionEqualToValue: (option: WorkflowActiveUser, current: WorkflowActiveUser) =>
      option.userId === current.userId,
    renderInput: (params: AutocompleteRenderInputParams) => (
      <TextField {...params} label={label} placeholder={placeholder} helperText={helperText} />
    ),
  };

  return multiple ? (
    <Autocomplete
      {...sharedProps}
      multiple
      value={selected}
      onChange={(_event, next) => onChange(next.map((user) => user.userId).join(","))}
    />
  ) : (
    <Autocomplete
      {...sharedProps}
      value={selected[0] ?? null}
      onChange={(_event, next) => onChange(next ? String(next.userId) : "")}
    />
  );
};

export default WorkflowUserPicker;
